import { useState } from "react";
import { Question } from "@phosphor-icons/react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "./ui/dialog";

/**
 * PageHelpButton — replaces the wordy collapsed "How this works" details
 * blocks that used to live at the top of every page. A small "?" icon
 * sitting inline with the page title opens a Shadcn dialog with 3-4 plain-
 * speak bullets. Same information, far less visual noise on the page.
 *
 * Props:
 *   title    — short H1 inside the dialog (e.g., "How Remittances work")
 *   bullets  — array of strings OR JSX nodes (one per bullet)
 *   testId   — data-testid root (button + dialog)
 *   footer   — optional small italic line under the bullets
 */
export default function PageHelpButton({ title, bullets, testId = "page-help", footer = null }) {
  const [open, setOpen] = useState(false);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          type="button"
          aria-label="How this page works"
          title="How this page works"
          className="inline-flex items-center justify-center w-8 h-8 border border-[#E5E5E5] hover:border-[#002FA7] hover:bg-[#002FA7]/[0.06] text-[#525252] hover:text-[#002FA7] transition-all"
          data-testid={`${testId}-button`}
        >
          <Question size={16} weight="bold" />
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-lg" data-testid={`${testId}-dialog`}>
        <DialogHeader>
          <DialogTitle className="font-heading text-lg text-[#002FA7]">{title}</DialogTitle>
          <DialogDescription className="sr-only">Plain-speak help for this page</DialogDescription>
        </DialogHeader>
        <ul className="space-y-3 text-sm text-[#171717] leading-relaxed">
          {bullets.map((b, i) => (
            <li
              key={i}
              className="pl-3 border-l-2 border-[#002FA7]/30"
              data-testid={`${testId}-bullet-${i}`}
            >
              {b}
            </li>
          ))}
        </ul>
        {footer && (
          <div className="text-[12px] text-[#525252] italic border-t pt-3 mt-1 border-[#E5E5E5]">
            {footer}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
